// @flow

import {useEffect} from 'react';
import {useStorage} from './StorageProvider';

/**
 * This works like useState, but uses an IndexedDB key-value store as a backing
 * store. It can be used to persist component state between visits, however:
 *
 * 1. It isn't sent to the server, so it's not available during the the initial render
 * 2. IndexedDB reads are async, to avoid blocking the main thread of the browser
 *
 * Because of these limitations, the first client-side render will always use the
 * defaultValue. This will result in a brief "flicker" of the defaultValue before showing
 * the loaded value.
 *
 * To prevent this flicker, the third return value is an "isLoading" flag, so the user of this hook can
 * determine what UI decisions to make to avoid this flicker.
 *
 * Also, because IndexedDB is written in an unencrypted format on the user's machine,
 * DO NOT use it to store personally-identifiable information such as user/merchant
 * names, IDs, keys, etc.
 */

export const usePersistentState = <T>(
  key: string,
  defaultValue: T,
): [T, (T) => Promise<void>, boolean] => {
  const context = useStorage();
  const storageValue = context.items[key];

  useEffect(() => {
    context.loadItem(key);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, []);

  const save = (newValue: T) => context.saveItem(key, newValue);

  // Consider the case where the item is not in the database. It should
  // First, return defaultValue and isLoading true
  // Second, return defaultValue and isLoading false
  const value =
    storageValue && storageValue.value ? storageValue.value : defaultValue;
  const isLoading = storageValue ? storageValue.isLoading : true;

  return [value, save, isLoading];
};
